import { Component, OnDestroy, OnInit } from '@angular/core';
import { Table } from '@interfaces/others';
import { Journey, ColorType, FindJourneyDto } from '@interfaces/rest/interfaces/journey';
import { NavController } from '@ionic/angular';
import { JourneyService } from '@services/journey.service';
import { Subscription } from 'rxjs';
import { DpUtilsService } from 'digitup-lib/dist/digitup-lib';
import { RestService } from '@services/rest.service';

@Component({
  selector: 'app-call-services',
  templateUrl: './call-services.component.html',
  styleUrls: ['./call-services.component.scss'],
})
export class CallServicesComponent implements OnInit, OnDestroy {

  columns: Table[] = [
    {
      field: 'id',
      name: 'id',
      width: 5
    }, {
      field: 'from',
      name: 'from',
      width: 20,
      class: 'align_left'
    }, {
      field: 'to',
      name: 'to',
      width: 20,
      class: 'align_left'
    },
    {
      field: 'createdAt',
      name: 'date',
      width: 15,
      type: 'datetime'
    }, {
      field: 'persons',
      name: 'travelers',
      width: 10
    }, {
      field: 'statusObj',
      name: 'status',
      width: 15,
      type: 'multiValColors'
    }, {
      field: 'nameLic',
      name: 'nameLic',
      width: 15
    }, {
      field: 'action',
      name: '',
      width: 20,
      type: 'actions',
      actions: [
        {
          type: 'view'
        },
        // {
        //   type: 'duplicate'
        //   // 🔥 Duplicar siempre está habilitado
        // },
        {
          type: 'delete',
          disabledField: 'canEdit' // 🔥 Deshabilitar si canEdit es false (solo para cancelar)
        }
      ]
    }];

  journeys: Journey[] = [];
  transformedJourneys: any[] = [];

  private journeysSubscription?: Subscription;

  constructor(
    private nav: NavController,
    private journeyService: JourneyService,
    private utils: DpUtilsService,
    private rest: RestService
  ) { }

  async ngOnInit() {
    this.subscribeToJourneys();
  }

  ngOnDestroy() {
    // Limpiar suscripciones para evitar memory leaks
    if (this.journeysSubscription) {
      this.journeysSubscription.unsubscribe();
    }
  }

  // Opción 4: Suscribirse al observable para updates automáticos
  subscribeToJourneys() {
    this.journeysSubscription = this.journeyService.getJourneys$().subscribe(
      (journeys: Journey[]) => {
        this.journeys = journeys;
        this.transformedJourneys = this.getJourneysForTable();
      }
    );
  }

  /**
   * 🔥 Obtiene el color según el estado del journey
   */
  private getStatusColor(status?: string): ColorType {
    if (!status) {
      return 'white';
    }

    // Estados rojos (cancelados/cerrados/error)
    if (['CLOSED', 'CANCELED_BY_USER', 'CANCELED_BY_DRIVER', 'CANCELED_BY_MANAGER', 'CANCEL', 'ERROR', 'EXPIRED'].includes(status)) {
      return 'red';
    }

    // Estados verdes (completados/confirmados)
    if (['TRIP_COMPLETED', 'DRIVER_CONFIRMED'].includes(status)) {
      return 'green';
    }

    // Estados azules (reservas/activos)
    if (['RECURRENT_RESERVE_NEW', 'RESERVE_WAITING', 'TRIP_STARTED'].includes(status)) {
      return 'blue';
    }

    // Por defecto blanco
    return 'white';
  }

  /**
   * 🔥 Verifica si un journey está en estado activo (permite editar/duplicar/cancelar)
   */
  private isJourneyActive(journey: Journey): boolean {
    return journey.status === 'NEW' ||
      journey.status === 'DRIVER_SEARCHING' ||
      journey.status === 'DRIVER_FOUND' ||
      journey.status === 'DRIVER_CONFIRMED' ||
      journey.status === 'DRIVER_ARRIVED' ||
      journey.status === 'TRIP_STARTED';
  }

  /**
   * 🔥 Transforma los journeys para la tabla, creando campos from y to y statusObj
   */
  getJourneysForTable(): any[] {
    if (!this.journeys || this.journeys.length === 0) {
      return [];
    }

    return this.journeys.map(journey => {
      // Determinar color según el estado
      const color = this.getStatusColor(journey.status);

      // Crear objeto transformado manteniendo el journey original
      const transformed: any = {
        ...journey,
        // Crear campo 'from' desde fromAddress
        from: journey.fromAddress
          ? `${journey.fromAddress.title || ''} - ${journey.fromAddress.description || ''}`.trim()
          : '',
        // Crear campo 'to' desde toAddress
        to: journey.toAddress
          ? `${journey.toAddress.title || ''} - ${journey.toAddress.description || ''}`.trim()
          : '',
        // Crear statusObj en formato correcto para multiValColors
        statusObj: journey.status ? [{
          name: journey.status,
          translatePath: 'services',
          color: color
        }] : [],
        // 🔥 Campo para deshabilitar acciones cuando el journey no está activo
        canEdit: this.isJourneyActive(journey)
      };

      return transformed;
    });
  }

  /**
   * 🔥 Maneja las acciones de la tabla de servicios
   */
  async doActionParent(ev: any) {
    console.log('doActionParent', ev);
    // El objeto ev.line es el journey transformado, pero conserva todas las propiedades originales
    const journey = ev.line as Journey;

    switch (ev.action) {
      case 'view':
        this.nav.navigateForward(`services/service-detail/${journey.id}`);
        break;
      case 'duplicate':
        await this.confirmDuplicate(journey);
        break;
      case 'delete':
        await this.confirmDelete(journey);
        break;
    }
  }

  /**
   * 🔥 Confirma si se quiere duplicar el servicio
   */
  async confirmDuplicate(journey: Journey) {
    const result = await this.utils.showAlert({
      header: 'Duplicar servicio',
      message: `¿Estás seguro de que quieres duplicar el servicio #${journey.id}?`,
      cancel: true,
      wait: true,
      cssClass: 'services-modal-confirm',
      buttonOk: 'Duplicar',
      buttonCancel: 'Cancelar'
    });

    if (result) {
      await this.duplicateService(journey);
    }
  }

  /**
   * 🔥 Confirma si se quiere eliminar el servicio usando AlertConfirmComponent
   */
  async confirmDelete(journey: Journey) {
    const result = await this.utils.showAlert({
      header: 'Cancelar servicio',
      message: `¿Estás seguro de que quieres cancelar el servicio #${journey.id}?`,
      cancel: true,
      wait: true,
      cssClass: 'services-modal-confirm',
      buttonOk: 'Cancelar servicio',
      buttonCancel: 'No cancelar'
    });

    if (result) {
      await this.deleteService(journey);
    }
  }

  /**
   * 🔥 Duplica el servicio
   */
  async duplicateService(journey: Journey) {
    if (!journey.id) {
      await this.utils.showToast({
        message: 'Error: No se pudo obtener el ID del servicio',
        cssClass: 'danger'
      });
      return;
    }

    try {
      // Crear FindJourneyDto desde el journey existente
      const dtoToDuplicate: FindJourneyDto = {
        fromLat: journey.fromLat,
        fromLng: journey.fromLng,
        fromAddress: journey.fromAddress || {
          title: '',
          description: ''
        },
        toLat: journey.toLat,
        toLng: journey.toLng,
        toAddress: journey.toAddress || {
          title: '',
          description: ''
        },
        persons: journey.persons || 1,
        extra: journey.extra || [],
        note: journey.note || '',
        reserve: journey.reserve || false,
        priority: journey.priority || false
      };

      // Si hay reserveDate, agregarlo
      if (journey.reserve && journey.reserveDate) {
        dtoToDuplicate.reserveDate = journey.reserveDate;
      }

      // Si hay userId, agregarlo
      if (journey.userId) {
        dtoToDuplicate.toAnotherUser = journey.userId;
      }

      await this.utils.showLoading({ duration: 20000 });
      const result = await this.rest.findJourney(dtoToDuplicate);
      await this.utils.hideLoading();

      if (result.ok) {
        await this.utils.showToast({
          message: 'Servicio duplicado correctamente',
          cssClass: 'success'
        });

        // 🔥 Refrescar los datos del usuario para actualizar la lista
        // El componente se actualizará automáticamente gracias a la suscripción
      } else {
        await this.utils.showToast({
          message: result.message || 'Error al duplicar el servicio',
          cssClass: 'danger'
        });
      }
    } catch (error) {
      await this.utils.hideLoading();
      console.error('Error al duplicar servicio:', error);
      await this.utils.showToast({
        message: 'Error al duplicar el servicio',
        cssClass: 'danger'
      });
    }
  }

  /**
   * 🔥 Elimina el servicio (cancela el journey)
   */
  async deleteService(journey: Journey) {
    if (!journey.id) {
      await this.utils.showToast({
        message: 'Error: No se pudo obtener el ID del servicio',
        cssClass: 'danger'
      });
      return;
    }

    try {
      await this.utils.showLoading();
      const resultAction = await this.rest.cancelTotalJourney(journey.id, 'AFTER_ACCEPT');
      await this.utils.hideLoading();

      if (resultAction.ok) {
        await this.utils.showToast({
          message: 'Servicio cancelado',
          cssClass: 'success'
        });

        // 🔥 Refrescar los datos del usuario para actualizar la lista
        // El componente se actualizará automáticamente gracias a la suscripción
      } else {
        await this.utils.showToast({
          message: resultAction.message || 'Error al cancelar el servicio',
          cssClass: 'danger'
        });
      }
    } catch (error) {
      await this.utils.hideLoading();
      console.error('Error al cancelar servicio:', error);
      await this.utils.showToast({
        message: 'Error al cancelar el servicio',
        cssClass: 'danger'
      });
    }
  }
}
